import { action, computed, makeObservable, observable } from 'mobx';
import { AwardGroupsSummary, AwardJudging, AwardProgramStatistic, AwardSummary, Group, NominationJudgedSummary, ProgramRegion, RegionAwardSummary } from '../../models';
import { getCountryAwardJudgingKey } from '../shared-components/award-judging-box-section/country-award-judging-key';

export class ProgramDashboardPageUiStore {
  @observable awardProgramStatistic?: AwardProgramStatistic;

  @observable awardGroupsSummaries: AwardGroupsSummary[] = [];

  @observable regionAwardSummaries: RegionAwardSummary[] = [];

  @observable awardSummaries: AwardSummary[] = [];

  @observable groups: Group[] = [];

  @observable awardJudgings: AwardJudging[] = [];

  @observable countryAwardJudgings: AwardJudging[] = [];

  @observable awardJudgingAlertMessages = new Map<number, string>();

  @observable countryAwardJudgingAlertMessages = new Map<string, string>();

  @observable nominationJudgedSummaries: NominationJudgedSummary[] = [];

  @observable programRegions: ProgramRegion[] = [];

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.awardProgramStatistic = undefined;
    this.awardGroupsSummaries = [];
    this.regionAwardSummaries = [];
    this.awardSummaries = [];
    this.groups = [];
    this.awardJudgings = [];
    this.countryAwardJudgings = [];
    this.awardJudgingAlertMessages.clear();
    this.countryAwardJudgingAlertMessages.clear();
    this.nominationJudgedSummaries = [];
    this.programRegions = [];
  }

  /* Award Program Statistic */

  @action
  setAwardProgramStatistic(awardProgramStatistic?: AwardProgramStatistic): void {
    this.awardProgramStatistic = awardProgramStatistic;
  }

  @computed
  get numberOfCategoryAwardNominations(): number {
    return this.awardProgramStatistic?.numberOfCategoryAwardNominations ?? 0;
  }

  @computed
  get numberOfCountryAwardNominations(): number {
    return this.awardProgramStatistic?.numberOfCountryAwardNominations ?? 0;
  }

  @computed
  get numberOfSubmittedNominations(): number {
    return this.awardProgramStatistic?.numberOfSubmittedNominations ?? 0;
  }

  @computed
  get numberOfInProgressNominations(): number {
    return this.awardProgramStatistic?.numberOfInProgressNominations ?? 0;
  }

  @computed
  get numberOfPassedNominations(): number {
    return this.awardProgramStatistic?.numberOfPassedNominations ?? 0;
  }

  @computed
  get numberOfDisqualifiedNominations(): number {
    return this.awardProgramStatistic?.numberOfDisqualifiedNominations ?? 0;
  }

  @computed
  get numberOfRegionAwardNominations(): number {
    return this.awardProgramStatistic?.numberOfRegionAwardNominations ?? 0;
  }

  @computed
  get numberOfSubmittedRegionAwardNominations(): number {
    return this.awardProgramStatistic?.numberOfSubmittedRegionAwardNominations ?? 0;
  }

  @computed
  get numberOfInprogressRegionAwardNominations(): number {
    return this.awardProgramStatistic?.numberOfInProgressRegionAwardNominations ?? 0;
  }

  @computed
  get numberOfPassedRegionAwardNominations(): number {
    return this.awardProgramStatistic?.numberOfPassedRegionAwardNominations ?? 0;
  }

  @computed
  get numberOfDisqualifiedRegionAwardNominations(): number {
    return this.awardProgramStatistic?.numberOfDisqualifiedRegionAwardNominations ?? 0;
  }

  /* Award Summaries */

  @action
  setAwardGroupsSummaries(awardGroupsSummaries: AwardGroupsSummary[]): void {
    this.awardGroupsSummaries = awardGroupsSummaries;
  }

  @action
  setRegionAwardSummaries(regionAwardSummaries: RegionAwardSummary[]): void {
    this.regionAwardSummaries = regionAwardSummaries;
  }

  @action
  setAwardSummaries(awardSummaries: AwardSummary[]): void {
    this.awardSummaries = awardSummaries;
  }

  /* Groups */

  @action
  setGroups(groups: Group[]): void {
    this.groups = groups;
  }

  /* Program Regions */

  @action
  setProgramRegions(programRegions: ProgramRegion[]): void {
    this.programRegions = programRegions;
  }

  /* Judge */

  @action
  setAwardJudgings(awardJudgings: AwardJudging[]): void {
    this.awardJudgings = awardJudgings.filter(awardJudging => awardJudging.countryId === null);
    this.countryAwardJudgings = awardJudgings.filter(awardJudging => awardJudging.countryId !== null);
  }

  @action
  setAwardJudgingAlertMessage(awardId: number, message: string): void {
    this.awardJudgingAlertMessages.set(awardId, message);
  }

  @action
  setCountryAwardJudgingAlertMessage(awardId: number, countryId: number, message: string): void {
    this.countryAwardJudgingAlertMessages.set(getCountryAwardJudgingKey(awardId, countryId), message);
  }

  @action
  clearAwardJudgingAlertMessage(awardId: number): void {
    this.awardJudgingAlertMessages.delete(awardId);
  }

  @action
  clearCountryAwardJudgingAlertMessage(awardId: number, countryId: number): void {
    this.countryAwardJudgingAlertMessages.delete(getCountryAwardJudgingKey(awardId, countryId));
  }

  @computed
  get hasJudgings(): boolean {
    return this.awardJudgings.length > 0 || this.countryAwardJudgings.length > 0;
  }

  @action
  setNominationJudgedSummaries(nominationJudgedSummaries: NominationJudgedSummary[]): void {
    this.nominationJudgedSummaries = nominationJudgedSummaries;
  }

  @computed
  get hasNominationJudgedSummaries(): boolean {
    return this.nominationJudgedSummaries.length > 0;
  }
}
